import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Globe, MapPin, Mail, Phone, ExternalLink, Star, Quote, Building2, ChevronLeft, ChevronRight, X, Play, Loader2, ArrowLeft, Clock, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getVideoEmbed = (url) => {
  if (!url) return "";
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return "";
};

const getVideoThumb = (url) => {
  if (!url) return "";
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
  return "";
};

export default function CompanyPublicProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  const [compNews, setCompNews] = useState({ news: [], total: 0 });
  const [compNewsPage, setCompNewsPage] = useState(1);
  const [selectedCompNews, setSelectedCompNews] = useState(null);

  useEffect(() => {
    axios.get(`${API}/companies/${slug}`)
      .then((r) => { setProfile(r.data); fetchCompNews(slug, 1); })
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const fetchCompNews = (s, p) => {
    axios.get(`${API}/companies/${s}/news?page=${p}`).then(r => { setCompNews(r.data); setCompNewsPage(p); }).catch(() => {});
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-stone-300" /></div>;
  if (!profile) return (
    <div className="text-center py-32">
      <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Firma Bulunamadı</h1>
      <Link to="/" className="text-[#1B4F72] hover:underline">Ana sayfaya dön</Link>
    </div>
  );

  const c = profile.contact || {};
  const s = profile.social || {};

  return (
    <div data-testid="company-profile" className="bg-white min-h-screen">

      {/* ===== HERO: Clean, spacious ===== */}
      <section className="bg-stone-950 text-white relative overflow-hidden">
        {profile.cover_image_url && (
          <div className="absolute inset-0">
            <img src={profile.cover_image_url} alt="" className="w-full h-full object-cover opacity-30" />
          </div>
        )}
        <div className="relative max-w-screen-lg mx-auto px-6 md:px-12 py-20 md:py-28 flex items-center gap-8">
          {/* Logo */}
          <div className="w-24 h-24 md:w-32 md:h-32 bg-white rounded-sm flex items-center justify-center flex-shrink-0 shadow-xl">
            {profile.logo_url
              ? <img src={profile.logo_url} alt={profile.company_name} className="w-full h-full object-contain p-3" />
              : <Building2 className="w-10 h-10 text-stone-300" />
            }
          </div>
          {/* Title */}
          <div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {profile.company_name}
            </h1>
            {c.city && (
              <p className="text-stone-400 mt-3 flex items-center gap-1.5 text-sm">
                <MapPin className="w-4 h-4" />
                {c.city}{c.country ? `, ${c.country}` : ""}
              </p>
            )}
            {profile.services?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {profile.services.slice(0, 5).map((sv, i) => (
                  <span key={i} className="px-3 py-1 bg-white/10 text-white/80 text-xs font-mono uppercase tracking-wider">{sv}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===== CONTACT BAR ===== */}
      {(c.email || c.phone || c.website) && (
        <section className="bg-stone-50 border-b border-stone-200">
          <div className="max-w-screen-lg mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-6 text-sm text-stone-600">
            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-[#1B4F72] transition-colors"><Mail className="w-4 h-4 text-stone-400" />{c.email}</a>}
            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 hover:text-[#1B4F72] transition-colors"><Phone className="w-4 h-4 text-stone-400" />{c.phone}</a>}
            {c.website && <a href={c.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#1B4F72] transition-colors"><ExternalLink className="w-4 h-4 text-stone-400" />{c.website}</a>}
            {c.address && <span className="flex items-center gap-2 text-stone-400"><MapPin className="w-4 h-4" />{c.address}</span>}
            <div className="ml-auto flex gap-4">
              {s.linkedin && <a href={s.linkedin} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-[#0077B5] font-mono text-xs uppercase">LinkedIn</a>}
              {s.instagram && <a href={s.instagram} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-[#E1306C] font-mono text-xs uppercase">Instagram</a>}
            </div>
          </div>
        </section>
      )}

      {/* ===== MAIN CONTENT — single column, generous spacing ===== */}
      <div className="max-w-screen-lg mx-auto px-6 md:px-12">

        {/* About */}
        {profile.about && (
          <section className="py-16 md:py-20">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#1B4F72] mb-4">Hakkımızda</p>
            <p className="text-lg md:text-xl text-stone-700 leading-relaxed max-w-3xl whitespace-pre-wrap">
              {profile.about}
            </p>
          </section>
        )}

        {/* Countries & Cities */}
        {(profile.countries?.length > 0 || profile.cities?.length > 0) && (
          <>
            <Separator />
            <section className="py-12">
              {profile.countries?.length > 0 && (
                <div className="mb-8">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#1B4F72] mb-4">Hizmet Verilen Ülkeler</p>
                  <div className="flex flex-wrap gap-3">
                    {profile.countries.map((country, i) => (
                      <span key={i} className="inline-flex items-center gap-2 px-4 py-2 bg-stone-50 text-stone-700 text-sm border border-stone-200">
                        <Globe className="w-3.5 h-3.5 text-[#003049]" />
                        {country}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {profile.cities?.length > 0 && (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#1B4F72] mb-4">Hizmet Verilen Şehirler</p>
                  <div className="flex flex-wrap gap-3">
                    {profile.cities.map((city, i) => (
                      <span key={i} className="inline-flex items-center gap-2 px-4 py-2 bg-stone-50 text-stone-700 text-sm border border-stone-200">
                        <MapPin className="w-3.5 h-3.5 text-stone-400" />
                        {city}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          </>
        )}

        {/* Company News */}
        {compNews.news.length > 0 && (
          <>
            <Separator />
            <section className="py-16">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#1B4F72] mb-8">Haberler</p>
              {selectedCompNews ? (
                <div>
                  <button onClick={() => setSelectedCompNews(null)} className="flex items-center gap-2 text-sm text-stone-500 hover:text-[#1B4F72] mb-6 font-mono uppercase tracking-widest"><ArrowLeft className="w-4 h-4" /> Haberlere Dön</button>
                  {selectedCompNews.image_url && <div className="aspect-[21/9] max-w-3xl overflow-hidden mb-6"><img src={selectedCompNews.image_url} alt="" className="w-full h-full object-cover" /></div>}
                  <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{selectedCompNews.title}</h2>
                  <p className="text-sm text-stone-500 font-mono mb-6">{new Date(selectedCompNews.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}</p>
                  <Separator className="mb-6" />
                  <div className="max-w-3xl text-stone-700 leading-relaxed text-lg whitespace-pre-wrap">{selectedCompNews.content}</div>
                  {selectedCompNews.gallery_images?.length > 0 && <div className="grid grid-cols-4 gap-2 mt-8 max-w-3xl">{selectedCompNews.gallery_images.map((img,i) => <div key={i} className="aspect-[4/3]"><img src={img} alt="" className="w-full h-full object-cover" /></div>)}</div>}
                </div>
              ) : (
                <>
                  {compNews.news.map(n => (
                    <button key={n.id} onClick={() => setSelectedCompNews(n)} className="w-full text-left border-b border-stone-200 pb-5 mb-5 hover:bg-stone-50 -mx-3 px-3 py-2 transition-colors group">
                      <div className="flex gap-4">
                        {n.image_url && <img src={n.image_url} alt="" className="w-36 h-24 object-cover flex-shrink-0" />}
                        <div><h3 className="text-lg font-bold text-stone-900 group-hover:text-[#1B4F72] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>{n.title}</h3>
                        <p className="text-sm text-stone-500 mt-1 line-clamp-2">{n.summary || n.content}</p>
                        <span className="font-mono text-[9px] text-stone-400 mt-2 block">Devamını oku →</span></div>
                      </div>
                    </button>
                  ))}
                  {compNews.total > 5 && <div className="flex justify-center gap-2"><Button onClick={() => fetchCompNews(slug, compNewsPage-1)} disabled={compNewsPage<=1} variant="outline" size="sm" className="rounded-none">Önceki</Button><span className="text-xs text-stone-500 py-2">{compNewsPage}/{Math.ceil(compNews.total/5)}</span><Button onClick={() => fetchCompNews(slug, compNewsPage+1)} disabled={compNewsPage>=Math.ceil(compNews.total/5)} variant="outline" size="sm" className="rounded-none">Sonraki</Button></div>}
                </>
              )}
            </section>
          </>
        )}

        {/* Projects */}
        {profile.projects?.length > 0 && (
          <>
            <Separator />
            <section className="py-16 md:py-20">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#1B4F72] mb-8">Referans Projeler</p>
              <div className="space-y-0">
                {profile.projects.map((proj, projIdx) => {
                  const images = proj.images || [];
                  const videoUrl = proj.video_url || "";
                  const videoEmbed = getVideoEmbed(videoUrl);
                  const videoThumb = getVideoThumb(videoUrl);

                  return (
                    <div key={proj.id}>
                      {projIdx > 0 && <Separator className="my-10" />}
                      {/* Project images — uniform grid, max 1/3 for single */}
                      {images.length > 0 && (
                        <div className="grid grid-cols-3 gap-2 mb-6">
                          {images.slice(0, 6).map((img, i) => (
                            <div
                              key={i}
                              className="aspect-[4/3] overflow-hidden bg-stone-100 cursor-pointer relative group"
                              onClick={() => setLightbox({ open: true, images, index: i })}
                            >
                              <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Video */}
                      {videoUrl && (
                        <div className="mb-6">
                          {videoEmbed ? (
                            <div className="aspect-video max-w-lg">
                              <iframe src={videoEmbed} className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title="video" />
                            </div>
                          ) : videoThumb ? (
                            <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="block max-w-xs relative group">
                              <img src={videoThumb} alt="" className="w-full aspect-video object-cover" />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                                <Play className="w-12 h-12 text-white fill-white" />
                              </div>
                            </a>
                          ) : (
                            <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1B4F72] hover:underline flex items-center gap-1">
                              <Play className="w-4 h-4" /> Video İzle
                            </a>
                          )}
                        </div>
                      )}
                      {/* Project info */}
                      <h3 className="text-xl md:text-2xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {proj.title}
                      </h3>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-stone-500">
                        {proj.fair_name && <span>{proj.fair_name}</span>}
                        {proj.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{proj.location}</span>}
                        {proj.stand_size && <span>{proj.stand_size}</span>}
                        {proj.year && <span>{proj.year}</span>}
                      </div>
                      {proj.description && (
                        <p className="text-stone-600 mt-4 leading-relaxed max-w-2xl">{proj.description}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {/* Lightbox Modal */}
        <Dialog open={lightbox.open} onOpenChange={(o) => !o && setLightbox({ ...lightbox, open: false })}>
          <DialogContent className="max-w-4xl p-0 bg-black border-0 rounded-none">
            <div className="relative">
              <img src={lightbox.images[lightbox.index]} alt="" className="w-full max-h-[80vh] object-contain" />
              {/* Nav arrows */}
              {lightbox.images.length > 1 && (
                <>
                  <button
                    onClick={() => setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.images.length) % lightbox.images.length })}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-2 transition-colors"
                  ><ChevronLeft className="w-6 h-6" /></button>
                  <button
                    onClick={() => setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.images.length })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-2 transition-colors"
                  ><ChevronRight className="w-6 h-6" /></button>
                </>
              )}
              {/* Counter */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 text-xs font-mono">
                {lightbox.index + 1} / {lightbox.images.length}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Clients */}
        {profile.clients?.length > 0 && (
          <>
            <Separator />
            <section className="py-16">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#1B4F72] mb-8">Müşterilerimiz</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-6">
                {profile.clients.map((client) => (
                  <div key={client.id} className="flex items-center justify-center p-4 bg-stone-50 aspect-[3/2]">
                    {client.logo_url
                      ? <img src={client.logo_url} alt={client.name} className="max-w-full max-h-full object-contain opacity-70 hover:opacity-100 transition-opacity" />
                      : <span className="text-xs text-stone-400 text-center">{client.name}</span>
                    }
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Testimonials */}
        {profile.testimonials?.length > 0 && (
          <>
            <Separator />
            <section className="py-16 md:py-20">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#1B4F72] mb-8">Müşteri Yorumları</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {profile.testimonials.map((t) => (
                  <div key={t.id} className="relative pl-6 border-l-2 border-[#1B4F72]">
                    <p className="text-stone-700 italic leading-relaxed text-lg">
                      "{t.text}"
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                      <div className="flex">
                        {[1,2,3,4,5].map((star) => (
                          <Star key={star} className={`w-3.5 h-3.5 ${star <= (t.rating || 5) ? "text-[#D4AF37] fill-[#D4AF37]" : "text-stone-200"}`} />
                        ))}
                      </div>
                      <span className="text-sm font-semibold text-stone-900">{t.author}</span>
                      {t.company && <span className="text-sm text-stone-400">— {t.company}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>

      {/* ===== FOOTER CTA ===== */}
      <section className="bg-stone-950 text-white">
        <div className="max-w-screen-lg mx-auto px-6 md:px-12 py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {profile.company_name} ile İletişime Geçin
          </h2>
          {c.email && (
            <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 px-8 py-3 bg-[#1B4F72] hover:bg-[#154360] text-white font-mono text-xs uppercase tracking-widest transition-colors mt-4">
              <Mail className="w-4 h-4" /> E-posta Gönder
            </a>
          )}
          {c.phone && (
            <a href={`tel:${c.phone}`} className="inline-flex items-center gap-2 px-8 py-3 border border-white/30 text-white font-mono text-xs uppercase tracking-widest ml-3 hover:bg-white/10 transition-colors mt-4">
              <Phone className="w-4 h-4" /> {c.phone}
            </a>
          )}
        </div>
      </section>
    </div>
  );
}
